/**
 * The AI pill (TMMAPP-0121 §B, TMM-B9 profile 1.4.3): the only way a surface marks what a model
 * produced. The Lucide `sparkles` icon and "AI" in the prototype's small tag chrome (`Pill`'s grey
 * wash, `text2` on `surface2`, a guaranteed pair); it takes no status tone. A `confidence` (0–1, or
 * a served percentage) makes it one pill in words: "AI · High confidence" (0.9 and above), "AI ·
 * Medium confidence" (0.6 and above), "AI · Low confidence" — never a separate confidence chip or a
 * bare percentage. `compact` is the icon alone for a dense table row; its tooltip and accessible
 * name say what the AI did there (`describes`, TMMAPP-0123 item 24b: "Payee name suggested by AI",
 * "Allocated by AI", "Category set by AI", "Matched by AI"), and "Made by AI" only as the fallback.
 * `NoAiPill` ("No AI", the same chrome without the icon) is only for a list that mixes both kinds.
 *
 * The caller decides from the served provenance field (`source`, `origin`, `actorKind`, or an
 * AI-only endpoint), never from the feature, the screen or the text.
 */
import { Box, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AiIcon } from '@/components/icons';
import { Pill } from '@/components/Pill';
import { resolveTokens } from '@/theme/theme';

const AI_HIGH_CONFIDENCE = 0.9;
const AI_MEDIUM_CONFIDENCE = 0.6;

type ConfidenceBand = 'high' | 'medium' | 'low';

/** A confidence's band; a value above 1 is read as a percentage. */
function aiConfidenceBand(confidence: number): ConfidenceBand {
  const fraction = confidence <= 1 ? confidence : confidence / 100;
  if (fraction >= AI_HIGH_CONFIDENCE) return 'high';
  if (fraction >= AI_MEDIUM_CONFIDENCE) return 'medium';
  return 'low';
}

/** What the AI did on a surface, for the compact mark's tooltip and name. */
export type AiMarkSubject = 'payeeName' | 'allocation' | 'category' | 'match';

const SUBJECT_KEYS = {
  payeeName: 'ai.pill.describes.payeeName',
  allocation: 'ai.pill.describes.allocation',
  category: 'ai.pill.describes.category',
  match: 'ai.pill.describes.match',
} as const satisfies Record<AiMarkSubject, string>;

export interface AiPillProps {
  /** The model's confidence (0–1); null or absent shows the plain pill. */
  confidence?: number | null | undefined;
  /** The icon alone, for a dense table row. */
  compact?: boolean | undefined;
  /** The compact mark's subject; absent reads "Made by AI". */
  describes?: AiMarkSubject | undefined;
}

/**
 * The AI pill's sparkles alone, decorative, for a control that wears the pill's chrome (TMMAPP-0164
 * item 15: Send to assistant). The icon stays this file's, so the pill remains the one AI mark.
 */
export function AiSparkles({ size }: { size: number }) {
  return <AiIcon size={size} aria-hidden />;
}

export function AiPill({ confidence = null, compact = false, describes }: AiPillProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  if (compact) {
    const name = describes !== undefined ? t(SUBJECT_KEYS[describes]) : t('ai.pill.madeBy');
    return (
      <Tooltip title={name}>
        <Box
          component="span"
          role="img"
          aria-label={name}
          data-paint="ai"
          sx={{ display: 'inline-flex', color: tokens.text2, flexShrink: 0 }}
        >
          <AiIcon size={14} />
        </Box>
      </Tooltip>
    );
  }
  const label =
    confidence === null || !Number.isFinite(confidence)
      ? t('ai.pill.label')
      : t(`ai.pill.${aiConfidenceBand(confidence)}`);
  return (
    <Pill wash="grey" data-paint="ai">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <AiIcon size={12} aria-hidden />
        {label}
      </Box>
    </Pill>
  );
}

/** "No AI": the AI pill's chrome without the icon, only in a list that mixes both kinds. */
export function NoAiPill() {
  const { t } = useTranslation();
  return (
    <Pill wash="grey" data-paint="no-ai">
      {t('ai.pill.noAi')}
    </Pill>
  );
}
